<template>
    <v-dialog
        v-model="isActive"
        width="700"
        persistent
        @click:outside.prevent="closeAndClear"
    >
        <div
            v-if="isActive"
            class="payment-add"
            :class="{ ['is-loading']: isLoading }"
        >
            <div class="payment-add__header">
                <h3 class="payment-add__title">
                    {{ $t('pages.payments.addPayment') }}
                </h3>
                <v-btn icon color="#000" large @click="closeAndClear">
                    <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
            </div>

            <div class="payment-add__body">
                <div>
                    <v-select
                        v-model="selectedPaymentType"
                        :items="paymentTypes"
                        :item-text="getTranslationForPaymentTypes"
                        item-value="payment_type"
                        :label="$t('pages.payments.selectPaymentType')"
                        outlined
                        dense
                        class="payment-select"
                    ></v-select>

                    <component
                        :is="paymentFormComponent"
                        v-bind="computedPaymentData"
                        @update-payment="updatePaymentData"
                    ></component>
                </div>

                <div class="payment-add__buttons">
                    <v-btn class="payment-add__save" @click="onSave">
                        {{ $t('pages.payments.save') }}
                    </v-btn>
                    <v-btn
                        v-if="selectedPaymentType === 1"
                        class="payment-add__save"
                        @click="onContinue"
                    >
                        {{ $t('pages.payments.addAnotherCheck') }}
                    </v-btn>
                </div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import modalsMixin from '@/mixins/modals.mixin'

import popUpTypes from '@/types/pop-up-types'
import languageMixin from '@/mixins/language.mixin'
import { getterTypes as getterTypesModals } from '@/store/modules/modals'
import { actionTypes as actionTypesConfirmer } from '@/store/modules/confirmer'
import {
    paymentsV2ActionTypes,
    paymentsV2GetterTypes,
} from '@/store/modules/payments_v2/types'
import PaymentCashForm from '@/components/payments/payment-cash-form.vue'
import PaymentCardForm from '@/components/payments/payment-card-form.vue'
import PaymentBankDebitForm from '@/components/payments/payment-bank-debit-form.vue'
import PaymentCheckForm from '@/components/payments/payment-check-form.vue'
import PaymentTransferForm from '@/components/payments/payment-transfer-form.vue'
import shortid from 'shortid'

const { paymentAdd } = popUpTypes

export default {
    name: 'PaymentModalAdd',
    components: {
        PaymentCashForm,
        PaymentCheckForm,
        PaymentCardForm,
        PaymentBankDebitForm,
        PaymentTransferForm,
    },
    mixins: [modalsMixin, languageMixin],
    data() {
        return {
            selfName: paymentAdd,
            isLoading: false,
            selectedPaymentType: null,
            paymentData: {},
        }
    },
    computed: {
        ...mapGetters('paymentsV2', {
            paymentTypes: paymentsV2GetterTypes.GET_PAYMENT_TYPES,
        }),
        ...mapGetters('modals', {
            getActiveModals: getterTypesModals.getModals,
        }),
        paymentFormComponent() {
            return this.getPaymentFormComponent(this.selectedPaymentType)
        },
        computedPaymentData: function () {
            return this.paymentData
        },
    },
    watch: {},
    mounted() {},
    methods: {
        //TODO: the methods should be refactored !
        ...mapActions('confirmer', {
            confirmHandle: actionTypesConfirmer.confirmHandle,
        }),
        ...mapActions('paymentsV2', {
            addPayment: paymentsV2ActionTypes.ADD_PAYMENT,
        }),
        getPaymentFormComponent: function (type_id) {
            if (type_id === 1) {
                return 'PaymentCheckForm'
            }

            if (type_id === 2) {
                return 'PaymentCashForm'
            }

            if (type_id === 9) {
                return 'PaymentTransferForm'
            }

            if (this.isBankDebit(type_id)) {
                return 'PaymentBankDebitForm'
            }

            if (this.isCard(type_id)) {
                return 'PaymentCardForm'
            }
        },
        updatePaymentData: function (newData) {
            this.paymentData = { ...this.paymentData, ...newData }
        },
        processPaymentData: function () {
            return {
                ...this.paymentData,
                payment_type: this.selectedPaymentType,
                local_id: shortid(),
            }
        },
        onContinue: async function () {
            const processedData = this.processPaymentData()
            await this.addPayment(processedData)

            const newDate = new Date(this.paymentData.due_date)
            newDate.setMonth(newDate.getMonth() + 1)

            this.paymentData = {
                ...this.paymentData,
                due_date: newDate.toISOString().split('T')[0],
                check_number: (
                    parseInt(this.paymentData.check_number || 0) + 1
                ).toString(),
            }
        },
        calculateNewDate: function (i) {
            const currentDate = new Date(this.paymentData.due_date)
            const currentDay = currentDate.getDate()
            const currentMonth = currentDate.getMonth()
            const currentYear = currentDate.getFullYear()

            const totalMonths = currentMonth + i
            const nextYear = currentYear + Math.floor(totalMonths / 12)
            const nextMonth = totalMonths % 12

            const dueDay = currentDay <= 15 ? 3 : 9

            return new Date(nextYear, nextMonth, dueDay)
                .toISOString()
                .slice(0, 10)
        },
        calculateInstallments: function (total, firstPayment, totalPayments) {
            let result = []

            firstPayment = Number(parseFloat(firstPayment).toFixed(2))
            result.push(firstPayment)

            let remaining = total - firstPayment
            let remainingPayments = totalPayments - 1

            // Base payment amount rounded down to cents
            let basePayment =
                Math.floor((remaining / remainingPayments) * 100) / 100

            // Total amount covered by base payments
            let baseTotal = basePayment * remainingPayments

            // The difference in cents
            let diff = Number((remaining - baseTotal).toFixed(2))
            let extraCents = Math.round(diff / 0.01)

            // Payments generation
            for (let i = 0; i < remainingPayments; i++) {
                let payment = basePayment
                if (i < extraCents) {
                    payment += 0.01
                }
                result.push(Number(payment.toFixed(2)))
            }

            return result
        },
        calculateNextAmount: function () {
            const totalAmount = this.paymentData.totally
            const initialAmount = this.paymentData.amount
            const quantity = this.paymentData.quantity

            if (quantity < 2) {
                return {
                    nextAmount: 0,
                    quantity,
                    initialAmount,
                }
            }

            const payments = this.calculateInstallments(
                totalAmount,
                initialAmount,
                quantity
            )

            return {
                nextAmount: payments.slice(1),
                quantity,
                initialAmount,
            }
        },
        clearPaymentData: function () {
            this.paymentData = {}
            this.selectedPaymentType = null
        },
        processCardPayments: async function () {
            const { nextAmount, quantity, initialAmount } =
                this.calculateNextAmount()

            const payments = []

            const subId = shortid()

            for (let i = 0; i < quantity; i++) {
                const payment = {
                    ...this.paymentData,
                    local_id: shortid(),
                    payment_type: this.selectedPaymentType,
                    due_date: this.calculateNewDate(i + 1),
                    amount: i === 0 ? initialAmount : nextAmount[i - 1],
                    sub_id: subId,
                    numberOfPayments: i + 1,
                }
                payments.push(payment)
            }

            for (const payment of payments) {
                await this.addPayment(payment)
            }
        },
        processBankDebitPayments: async function () {
            const { nextAmount, quantity, initialAmount } =
                this.calculateNextAmount()

            const payments = []

            const subId = shortid()

            for (let i = 0; i < quantity; i++) {
                const payment = {
                    ...this.paymentData,
                    local_id: shortid(),
                    payment_type: this.selectedPaymentType,
                    due_date: this.calculateNewDate(i + 1),
                    amount: i === 0 ? initialAmount : nextAmount[i - 1],
                    sub_id: subId,
                    numberOfPayments: i + 1,
                }
                payments.push(payment)
            }

            for (const payment of payments) {
                await this.addPayment(payment)
            }
        },
        onCardPayment: async function () {
            if (this.paymentData.quantity < 2) {
                const processedData = this.processPaymentData()
                await this.addPayment(processedData)

                return
            }

            await this.processCardPayments()
            await this.closeAndClear()
        },
        onBankDebitPayment: async function () {
            if (this.paymentData.quantity < 2) {
                const processedData = this.processPaymentData()
                await this.addPayment(processedData)

                return
            }

            await this.processBankDebitPayments()
            await this.closeAndClear()
        },
        isBankDebit: function (payment_type) {
            return payment_type === 100
        },
        isCard: function (payment_type) {
            return [4, 5, 6, 7, 8, 10, 11].includes(payment_type)
        },
        onSave: async function () {
            if (this.isCard(this.selectedPaymentType)) {
                await this.onCardPayment()

                await this.closeAndClear()
                return
            }

            if (this.isBankDebit(this.selectedPaymentType)) {
                await this.onBankDebitPayment()

                await this.closeAndClear()
                return
            }

            const processedData = this.processPaymentData()
            await this.addPayment(processedData)

            await this.closeAndClear()
        },
        closeAndClear: async function () {
            this.clearPaymentData()
            this.closeHandle()
        },
        getTranslationForPaymentTypes: function (item) {
            const translatedKey = `pages.payments.${item.payment_name}`
            const translatedText = this.$t(translatedKey)

            if (translatedText.includes('Translation not found for key')) {
                return item.payment_name
            }

            return translatedText
        },
    },
}
</script>

<style lang="scss">
.payment-add {
    background: $color-primary-light;
    position: relative;

    &.is-loading {
        overflow: hidden;
        max-height: 90vh;
    }

    &__title {
        font-size: 22px;
        font-weight: 400;
        color: #000;
    }

    &__header {
        padding: 22px 16px;
        border-bottom: 1px solid #eceeef;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__body {
        width: 100%;
        height: 100%;
        padding: 16px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    &__save {
        background: #37bc9b !important;
        color: white !important;
        padding: 16px !important;
        font-size: 16px !important;
        flex: 1 !important;

        &:nth-child(2) {
            background: #967adc !important;
        }
    }

    &__buttons {
        display: flex;
        justify-content: center;
        gap: 16px;
        width: 100%;
        align-items: center;
    }

    .payment-fields {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0;
        width: 100%;
    }

    .payment-field {
        max-width: 100% !important;
        min-width: 100%;
    }
}
</style>
